import { observer } from 'mobx-react-lite'
import { useEffect, useRef, useState } from 'react'
import {
  Button,
  ContainerHeader,
  Divider,
  EditableTitle,
  IconButton,
  variantActionsProps,
} from 'shared/ui'
import { layoutStore, type PageLayoutStore } from 'shared/layout'
import { AdditionalBreakpointsEnum } from 'shared/constants/breakpoints'
import { CAMPAIGN_NAME_LIMIT } from 'shared/constants/limits'
import { uiStore } from 'shared/store/uiStore'
import { CampaignStatus } from 'entities/Campaign/ui'
import { CampaignsPath } from 'entities/ExportRouters'
import { mobilePreviewModal } from 'widgets/MobilePreview'
import { Preview } from 'pages/campaigns/ui/CampaignView/ui/Preview/Preview'
import { useCampaignViewContext } from 'pages/campaigns/ui/CampaignView/context/context'
import { CampaignBlocker } from 'pages/campaigns/ui/CampaignBlocker'
import { CampaignEnrollContacts } from 'pages/campaigns/ui/CampaignEnrollContacts'
import { CampaignMessages } from 'pages/campaigns/ui/CampaignMessages'
import { CampaignErrorField } from 'pages/campaigns/ui/CampaignView/store/type'
import styles from './styles.module.scss'

export type ICampaignViewUiProps = {
  pageLayoutStore: PageLayoutStore
}

export const CampaignViewUi = observer(({ pageLayoutStore }: ICampaignViewUiProps) => {
  const {
    campaignBlockerStore,
    campaignMessagesStore,
    campaignEnrollContactsStore,
    validateTrigger,
    status,
    nameInputStore,
    onSettings,
    onSave,
    disabledSave,
    onPublish,
    isActive,
    openDisableModal,
  } = useCampaignViewContext()
  const messages = campaignMessagesStore.messages
  const refContent = useRef<HTMLDivElement>(null)
  const { focusOnMount, name, error, setName } = nameInputStore
  const {
    titleIconProps,
    headerMenuTooltipIconProps,
    setCustomCollapseWidth,
    headerMenuIconAction,
  } = pageLayoutStore
  const [loadingSave, setLoadingSave] = useState(false)
  const [loadingPublish, setLoadingPublish] = useState(false)
  const { isViewXs } = layoutStore

  useEffect(() => {
    setCustomCollapseWidth(AdditionalBreakpointsEnum['1280'])
    return () => {
      setCustomCollapseWidth(null)
    }
  }, [])

  const handleSave = async () => {
    setLoadingSave(true)
    await onSave()
    setLoadingSave(false)
  }

  const handlePublish = async () => {
    setLoadingPublish(true)
    await onPublish(isActive)
    setLoadingPublish(false)
  }

  useEffect(() => {
    if (validateTrigger && refContent.current) {
      const errorFields = refContent.current.querySelectorAll('[data-error]')
      let isScroll = false
      errorFields.forEach((field) => {
        if (!refContent.current) return

        if (field instanceof HTMLDivElement && field.dataset.error && !isScroll) {
          const error = field.dataset.error as CampaignErrorField
          if (error === CampaignErrorField.name || error === CampaignErrorField.enrollContacts) {
            isScroll = true
            refContent.current.scrollTop = 0
          }
          if (error === CampaignErrorField.messageStep) {
            isScroll = true
            field.scrollIntoView()
          }
        }
      })
    }
  }, [validateTrigger])

  return (
    <>
      <ContainerHeader
        style={{ height: '100%', minHeight: 'unset', padding: '0 20px' }}
        titleVariant='heading-md'
        titleIconProps={titleIconProps}
        actionIconTooltipProps={headerMenuTooltipIconProps}
        titleIconAction={headerMenuIconAction}
        isBorderBottom
        isTitleDivider
        rightTitleContent={
          status ? (
            <div className={styles.rightTitleContent}>
              <CampaignStatus status={status} />
            </div>
          ) : null
        }
        customTitle={
          <EditableTitle
            value={name}
            onChange={setName}
            error={error}
            limit={CAMPAIGN_NAME_LIMIT}
            isAutoFocus={focusOnMount}
          />
        }
        actionsProps={{
          actions: [
            {
              buttonComponent: (
                <>
                  <IconButton
                    ariaLabel='close_setting'
                    icon='close'
                    variant='contained'
                    color='secondary'
                    size='medium'
                    onClick={() =>
                      uiStore.changeRoute({
                        path: CampaignsPath.all,
                      })
                    }
                  />
                  <Divider
                    orientation='vertical'
                    sx={{ height: '16px !important', marginInline: '3.5px' }}
                  />
                </>
              ),
            },
            {
              buttonComponent: (
                <Button
                  contained={'primary'}
                  text={isActive ? 'Publish changes' : 'Activate'}
                  onClick={handlePublish}
                  disabled={loadingPublish || loadingSave}
                />
              ),
            },
            !isActive && {
              buttonComponent: (
                <Button
                  contained={'secondary'}
                  text={!status ? 'Save as draft' : 'Save changes'}
                  onClick={handleSave}
                  disabled={disabledSave || loadingSave || loadingPublish}
                />
              ),
            },
            {
              dropdownItemComponent: (onCloseMenu) => (
                <>
                  {isActive && (
                    <Button
                      typeBtn='menuItem'
                      text='Disable campaign'
                      onClick={() => {
                        openDisableModal()
                        onCloseMenu?.()
                      }}
                    />
                  )}
                  {isViewXs && (
                    <Button
                      typeBtn='menuItem'
                      text='See message preview'
                      onClick={() => {
                        mobilePreviewModal(<Preview orderedMessages={messages} />)
                        onCloseMenu?.()
                      }}
                    />
                  )}
                  <Button
                    typeBtn='menuItem'
                    text='Settings'
                    onClick={() => {
                      onSettings()
                      onCloseMenu?.()
                    }}
                  />
                </>
              ),
            },
          ],
          ...variantActionsProps.headerMedium,
          showItems: isActive ? 2 : 3,
          isStrictShowItems: true,
          reverse: true,
        }}
      />
      <div className={styles.wrap}>
        <CampaignBlocker store={campaignBlockerStore} />
        <div className={styles.left}>
          <div className={styles.content} ref={refContent}>
            <div className={styles.wrapBody}>
              <CampaignEnrollContacts store={campaignEnrollContactsStore} />
              <CampaignMessages store={campaignMessagesStore} />
            </div>
          </div>
        </div>
        <div className={styles.right}>
          <Preview orderedMessages={messages} />
        </div>
      </div>
    </>
  )
})
